/* Checking the presentation's comparative claims against the printed figures.
 *
 * A model reading four boards will occasionally invert a comparison — "the most
 * retail parking, at 23" when another board prints 39. The figures themselves are
 * copied verbatim and reliable, so the arithmetic can be done here, in code, and
 * any sentence that contradicts it flagged for the architect before the client
 * ever sees it. Never silently rewritten: it's the architect's presentation. */

import type { DesignOption } from "./types";

export interface ClaimIssue {
  where: string;          // which slide or option the sentence sits in
  sentence: string;
  metric: string;         // the figure it's talking about
  claimed: "most" | "least";
  truth: string;          // what the boards actually say
}

/* "third-highest of the four" is a rank, not a superlative — and it's fine. */
const ORDINAL = /\b(second|third|fourth|fifth|2nd|3rd|4th|5th)[-\s](most|highest|largest|least|fewest|lowest|smallest)\b/i;
const MOST = /\b(most|highest|largest|greatest|biggest|maximum)\b/i;
const LEAST = /\b(least|fewest|lowest|smallest|minimum)\b/i;
/** "of the four", "of the three" — a claim about the whole set. */
const OF_THE_SET = /\bof the (two|three|four|five|options|schemes|boards)\b/i;

const label0 = (ref: string) => (/^option/i.test(ref) ? ref : `option ${ref}`);
const numberIn = (v: string): number | null => {
  const m = v.replace(/,/g, "").match(/-?\d+(\.\d+)?/);
  return m ? parseFloat(m[0]) : null;
};

/** Words that identify which metric a sentence is talking about. */
function metricWords(label: string): string[] {
  return label.toLowerCase().replace(/[^a-z\s]/g, " ").split(/\s+/)
    .filter((w) => w.length > 3 && !["number", "lots", "the", "and", "area", "total"].includes(w));
}

function sentences(text: string): string[] {
  // no regex lookbehind here — Safari before 16.4 refuses to parse it, and
  // because this module sits in the main bundle, that was a blank page
  return text.replace(/([.!?])\s+/g, "$1\n").split("\n").map((s) => s.trim()).filter(Boolean);
}

/** Every comparative claim that the printed figures contradict. */
export function checkClaims(options: DesignOption[], texts: { where: string; text: string }[]): ClaimIssue[] {
  /* Build the truth table from the figures as printed. Only labels that appear
     on every option can be compared across the set. */
  const labels = new Map<string, { ref: string; n: number }[]>();
  for (const o of options)
    for (const m of o.metrics ?? []) {
      const n = numberIn(m.value);
      if (n == null) continue;
      const key = m.label.trim().toLowerCase();
      if (!labels.has(key)) labels.set(key, []);
      labels.get(key)!.push({ ref: o.ref, n });
    }

  const issues: ClaimIssue[] = [];
  for (const { where, text } of texts) {
    if (!text) continue;
    for (const s of sentences(text)) {
      if (ORDINAL.test(s)) continue;
      const wantsMost = MOST.test(s), wantsLeast = LEAST.test(s);
      /* "of the four" makes it a claim about the set; so does naming an option. */
      const namesAnOption = /option [A-Za-z0-9]/i.test(s);
      if ((!wantsMost && !wantsLeast) || (!OF_THE_SET.test(s) && !namesAnOption)) continue;
      /* Banning figures from the prose moved this error into words — "Option 3,
         holding the most warehouse parking" with no number in sight. So when a
         sentence names an option and claims a superlative, check THAT option's
         figure, not a number in the text. */
      const named = s.match(/\boption\s+([A-Z0-9]+)\b/i)?.[1];
      const figure = numberIn((s.match(/[\d][\d,]*(\.\d+)?/) ?? [""])[0] ?? "");

      for (const [label, vals] of labels) {
        if (vals.length < 2) continue;
        const words = metricWords(label);
        if (!words.length || !words.some((w) => s.toLowerCase().includes(w))) continue;
        /* Is the figure quoted actually the extreme it's claimed to be? */
        const max = Math.max(...vals.map((v) => v.n));
        const min = Math.min(...vals.map((v) => v.n));
        const near = (a: number, b: number) => Math.abs(a - b) < Math.max(1, Math.abs(b) * 0.001);
        /* The value the claim is really about: a figure quoted in the sentence,
           or the named option's own figure when the prose carries no numbers. */
        const byName = named
          ? vals.find((v) => v.ref.toLowerCase().replace(/^option\s*/, "") === named.toLowerCase())?.n
          : undefined;
        const subject = (figure != null && vals.some((v) => near(v.n, figure))) ? figure : byName;
        if (subject == null) continue;

        if (wantsMost && !near(subject, max)) {
          issues.push({ where, sentence: s, metric: label, claimed: "most",
            truth: `${subject.toLocaleString()} is not the highest — that's ${max.toLocaleString()} (${label0(vals.find((v) => v.n === max)!.ref)})` });
        } else if (wantsLeast && !near(subject, min)) {
          issues.push({ where, sentence: s, metric: label, claimed: "least",
            truth: `${subject.toLocaleString()} is not the lowest — that's ${min.toLocaleString()} (${label0(vals.find((v) => v.n === min)!.ref)})` });
        }
      }
    }
  }
  return issues;
}

/** Everything in a deck worth checking: slide bodies and the option prose. */
export function deckTexts(deck: { slides: { title: string; body?: string }[]; options: DesignOption[] }): { where: string; text: string }[] {
  return [
    ...deck.slides.map((s) => ({ where: s.title || "a slide", text: s.body ?? "" })),
    ...deck.options.flatMap((o) => [
      { where: `Option ${o.ref} — description`, text: o.description ?? "" },
      { where: `Option ${o.ref} — strengths`, text: (o.strengths ?? []).join(". ") },
      { where: `Option ${o.ref} — trade-offs`, text: (o.tradeoffs ?? []).join(". ") },
      { where: `Option ${o.ref} — decisions`, text: (o.decisions ?? []).join(". ") },
      { where: `Option ${o.ref} — cost note`, text: o.costNote ?? "" },
    ]),
  ];
}
